import { useCallback, useState } from 'react';

import {
  AdminSectionCard,
  ConfirmDialog,
  HelpAnchor,
  InlineError,
  InputField,
  SkeletonForm,
  Switch,
  TextareaField,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY, SECTION_DIVIDER } from '@/shared/ui-class';
import {
  ADMIN_SEED_ANCHOR_MS,
  SEED_MAINTENANCE_CONFIG,
  SEED_MAINTENANCE_SERVER_NOW,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import { AnnouncementBannerPanel } from './MaintenanceAnnouncementBannerPanel';
import {
  computeEffectiveOn,
  EMAIL_PATTERN,
  formatCountdown,
  formatMaintenanceInstant,
  localInputToUtcIso,
  MAINTENANCE_CONFIRM_WORD,
  MAINTENANCE_MAX_MESSAGE_LENGTH,
  mockArmTwoMinutes,
  type MaintenanceConfig,
  utcIsoToLocalInput,
} from './MaintenanceScene.transform';

function ScheduleCountdown({
  engagesAt,
  serverNow,
}: {
  engagesAt: string;
  serverNow: string;
}) {
  const engagesAtMs = Date.parse(engagesAt);
  const serverNowMs = Date.parse(serverNow);
  const initialRemainingMs = engagesAtMs - serverNowMs;
  const remainingMs = Math.max(0, initialRemainingMs);
  const secs = Math.ceil(remainingMs / 1000);

  if (remainingMs <= 0) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Schedule has tripped — maintenance is now effective.
      </p>
    );
  }

  return (
    <p className="text-xs text-muted">
      Engages in{' '}
      <span className="font-mono font-medium text-zinc-700 dark:text-zinc-300">
        {formatCountdown(secs)}
      </span>
    </p>
  );
}

function MaintenancePanelSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <AdminSectionCard title="Maintenance mode">
        <SkeletonForm fields={1} />
      </AdminSectionCard>
      <AdminSectionCard title="Lobby settings">
        <SkeletonForm fields={4} />
      </AdminSectionCard>
    </div>
  );
}

export function MaintenanceScene() {
  const [config, setConfig] = useState<MaintenanceConfig>(SEED_MAINTENANCE_CONFIG);
  const [serverNow, setServerNow] = useState<string>(SEED_MAINTENANCE_SERVER_NOW);
  const [loading] = useState(false);

  const [messageInput, setMessageInput] = useState(SEED_MAINTENANCE_CONFIG.message);
  const [expectedEndInput, setExpectedEndInput] = useState(
    SEED_MAINTENANCE_CONFIG.expectedEnd
      ? utcIsoToLocalInput(SEED_MAINTENANCE_CONFIG.expectedEnd)
      : '',
  );
  const [engagesAtInput, setEngagesAtInput] = useState(
    SEED_MAINTENANCE_CONFIG.engagesAt
      ? utcIsoToLocalInput(SEED_MAINTENANCE_CONFIG.engagesAt)
      : '',
  );
  const [supportEmailInput, setSupportEmailInput] = useState(
    SEED_MAINTENANCE_CONFIG.supportEmail ?? '',
  );

  const [pristine, setPristine] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [armBusy, setArmBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const serverNowForDisplay =
    serverNow !== '' ? serverNow : new Date(ADMIN_SEED_ANCHOR_MS).toISOString();
  const isEffectivelyOn =
    config.enabled ||
    (config.engagesAt != null &&
      Date.parse(serverNowForDisplay) >= Date.parse(config.engagesAt));

  const isScheduled = !config.enabled && config.engagesAt != null;

  const putConfig = useCallback((next: MaintenanceConfig) => {
    setConfig(next);
    setMessageInput(next.message);
    setExpectedEndInput(next.expectedEnd ? utcIsoToLocalInput(next.expectedEnd) : '');
    setEngagesAtInput(next.engagesAt ? utcIsoToLocalInput(next.engagesAt) : '');
    setSupportEmailInput(next.supportEmail ?? '');
    setPristine(true);
  }, []);

  const requestToggleOn = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const confirmToggleOn = useCallback(async () => {
    const trimmedMessage = messageInput.trim();
    if (trimmedMessage.length === 0) {
      setError('A message is required before enabling maintenance mode');
      return;
    }
    setToggleBusy(true);
    setError(null);
    const next: MaintenanceConfig = {
      ...config,
      enabled: true,
      engagesAt: null,
      message: trimmedMessage,
      expectedEnd: localInputToUtcIso(expectedEndInput),
      supportEmail: supportEmailInput.trim() || null,
    };
    putConfig(next);
    setToggleBusy(false);
  }, [config, expectedEndInput, messageInput, putConfig, supportEmailInput]);

  const toggleOff = useCallback(async () => {
    setToggleBusy(true);
    setError(null);
    const next: MaintenanceConfig = {
      ...config,
      enabled: false,
      engagesAt: null,
    };
    putConfig(next);
    setToggleBusy(false);
  }, [config, putConfig]);

  const handleToggle = useCallback(() => {
    if (isEffectivelyOn) {
      void toggleOff();
    } else {
      requestToggleOn();
    }
  }, [isEffectivelyOn, requestToggleOn, toggleOff]);

  const armTwoMinutes = useCallback(async () => {
    const trimmedMessage = messageInput.trim();
    if (trimmedMessage.length === 0) {
      setError('A message is required before scheduling maintenance');
      return;
    }
    setArmBusy(true);
    setError(null);
    const serverEngagesAt = mockArmTwoMinutes(ADMIN_SEED_ANCHOR_MS);
    const next: MaintenanceConfig = {
      ...config,
      enabled: false,
      engagesAt: serverEngagesAt,
      message: trimmedMessage,
      expectedEnd: localInputToUtcIso(expectedEndInput),
      supportEmail: supportEmailInput.trim() || null,
    };
    putConfig(next);
    setServerNow(SEED_MAINTENANCE_SERVER_NOW);
    setArmBusy(false);
  }, [config, expectedEndInput, messageInput, putConfig, supportEmailInput]);

  const cancelSchedule = useCallback(async () => {
    setArmBusy(true);
    setError(null);
    const next: MaintenanceConfig = {
      ...config,
      enabled: false,
      engagesAt: null,
    };
    putConfig(next);
    setArmBusy(false);
  }, [config, putConfig]);

  const saveSettings = useCallback(async () => {
    const trimmedEmail = supportEmailInput.trim();
    if (trimmedEmail.length > 0 && !EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Enter a valid support email address');
      return;
    }
    const trimmedMessage = messageInput.trim();
    const nextEngagesAt = localInputToUtcIso(engagesAtInput);
    const inputUnchanged =
      config.engagesAt !== null && engagesAtInput === utcIsoToLocalInput(config.engagesAt);
    const resolvedEngagesAt: string | null = inputUnchanged ? config.engagesAt : nextEngagesAt;

    const wouldBeEffectivelyOn =
      config.enabled ||
      (resolvedEngagesAt != null &&
        serverNow !== '' &&
        Date.parse(serverNow) >= Date.parse(resolvedEngagesAt));
    if (wouldBeEffectivelyOn && trimmedMessage.length === 0) {
      setError(
        'A message is required while maintenance is active or scheduled. To turn it off, use Cancel schedule.',
      );
      return;
    }
    if (trimmedMessage.length > MAINTENANCE_MAX_MESSAGE_LENGTH) {
      setError(`Message must be ${MAINTENANCE_MAX_MESSAGE_LENGTH} characters or fewer`);
      return;
    }
    if (resolvedEngagesAt !== null) {
      const engagesMs = Date.parse(resolvedEngagesAt);
      const nowMs = serverNow ? Date.parse(serverNow) : ADMIN_SEED_ANCHOR_MS;
      const isAlreadyTripped =
        config.engagesAt === resolvedEngagesAt && engagesMs < nowMs;
      if (!isAlreadyTripped && engagesMs < nowMs) {
        setError(
          "Schedule time must be in the future. To cancel an armed schedule, use 'Cancel schedule'.",
        );
        return;
      }
    }

    const next: MaintenanceConfig = {
      ...config,
      message: trimmedMessage,
      expectedEnd: localInputToUtcIso(expectedEndInput),
      engagesAt: resolvedEngagesAt,
      supportEmail: trimmedEmail || null,
    };
    setBusy(true);
    setError(null);
    putConfig(next);
    setBusy(false);
  }, [
    config,
    engagesAtInput,
    expectedEndInput,
    messageInput,
    putConfig,
    serverNow,
    supportEmailInput,
  ]);

  const engagesAtFormatted = config.engagesAt
    ? formatMaintenanceInstant(config.engagesAt)
    : null;
  const expectedEndFormatted = config.expectedEnd
    ? formatMaintenanceInstant(config.expectedEnd)
    : null;

  const panel = loading ? (
    <MaintenancePanelSkeleton />
  ) : (
    <>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Enable maintenance mode?"
        description="All non-admin users will be redirected to the maintenance lobby immediately. New chat turns will be rejected with a 503 until maintenance is turned off."
        confirmLabel="Enable maintenance"
        tone="danger"
        confirmation={{
          expected: MAINTENANCE_CONFIRM_WORD,
          label: `Type ${MAINTENANCE_CONFIRM_WORD} to confirm`,
          placeholder: MAINTENANCE_CONFIRM_WORD,
          caseSensitive: true,
        }}
        onConfirm={confirmToggleOn}
      />

      <div className="space-y-6">
        <AdminSectionCard
          title="Maintenance mode"
          description="When on, all non-admin users are redirected to the maintenance lobby and new chat turns are rejected (503). In-flight sessions are not killed."
          actions={
            <Switch
              id="maintenance-enabled"
              checked={isEffectivelyOn}
              onCheckedChange={handleToggle}
              disabled={toggleBusy}
              aria-label="Maintenance mode"
            />
          }
        >
          {config.enabled ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              Maintenance mode is active. Non-admin users are currently in the lobby.
            </p>
          ) : isScheduled ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Maintenance is scheduled.{' '}
                {engagesAtFormatted ? (
                  <>
                    Engages at{' '}
                    <span className="font-medium">{engagesAtFormatted.local}</span>{' '}
                    <span className="text-amber-500 dark:text-amber-500">
                      ({engagesAtFormatted.utc})
                    </span>
                  </>
                ) : null}
              </p>
              {serverNow && config.engagesAt ? (
                <div className="mt-1">
                  <ScheduleCountdown engagesAt={config.engagesAt} serverNow={serverNow} />
                </div>
              ) : null}
              <button
                type="button"
                disabled={armBusy}
                onClick={() => void cancelSchedule()}
                className="mt-2 text-xs font-medium text-amber-700 underline hover:text-amber-900 disabled:opacity-50 dark:text-amber-400 dark:hover:text-amber-200"
              >
                {armBusy ? 'Cancelling…' : 'Cancel schedule'}
              </button>
            </div>
          ) : null}
        </AdminSectionCard>

        <AdminSectionCard title="Lobby settings">
          <div className="space-y-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Message</span>
              <TextareaField
                value={messageInput}
                onChange={(e) => {
                  setMessageInput(e.target.value);
                  setPristine(false);
                  setError(null);
                }}
                placeholder="e.g. We are performing a scheduled database upgrade. We expect to be back in approximately 2 hours."
                rows={3}
                maxLength={MAINTENANCE_MAX_MESSAGE_LENGTH}
              />
              <span className="text-right text-xs text-muted">
                {messageInput.length}/{MAINTENANCE_MAX_MESSAGE_LENGTH}
              </span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Expected end time{' '}
                <span className="font-normal text-muted">(optional)</span>
              </span>
              <InputField
                type="datetime-local"
                value={expectedEndInput}
                onChange={(e) => {
                  setExpectedEndInput(e.target.value);
                  setPristine(false);
                  setError(null);
                }}
              />
              {expectedEndFormatted ? (
                <span className="text-xs text-muted">
                  Stored as: {expectedEndFormatted.local}{' '}
                  <span className="text-zinc-300 dark:text-zinc-600">
                    ({expectedEndFormatted.utc})
                  </span>
                </span>
              ) : null}
              <span className="text-xs text-muted">
                Shown in the lobby. If this time passes, the lobby shows an honest overrun message
                instead.
              </span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Support email{' '}
                <span className="font-normal text-muted">(optional)</span>
              </span>
              <InputField
                type="email"
                value={supportEmailInput}
                onChange={(e) => {
                  setSupportEmailInput(e.target.value);
                  setPristine(false);
                  setError(null);
                }}
                placeholder="support@example.com"
              />
              <span className="text-xs text-muted">
                Shown as a mailto link in the maintenance lobby.
              </span>
            </label>

            {error ? <InlineError>{error}</InlineError> : null}

            <button
              type="button"
              disabled={pristine || busy}
              onClick={() => void saveSettings()}
              className={BUTTON_PRIMARY}
            >
              {busy ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </AdminSectionCard>

        {!config.enabled ? (
          <AdminSectionCard title="Schedule maintenance">
            <div className="space-y-4">
              <p className="text-sm text-muted">
                Schedule maintenance to engage automatically at a future time. The server evaluates
                the schedule lazily — no background job. Closing this tab does not cancel or delay
                the scheduled engage.
              </p>

              {!isScheduled ? (
                <button
                  type="button"
                  disabled={armBusy || messageInput.trim().length === 0}
                  onClick={() => void armTwoMinutes()}
                  className="rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-700 disabled:opacity-40 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-300"
                >
                  {armBusy ? 'Scheduling…' : 'Enable in 2 minutes'}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={armBusy}
                  onClick={() => void cancelSchedule()}
                  className="rounded-lg border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300"
                >
                  {armBusy ? 'Cancelling…' : 'Cancel schedule'}
                </button>
              )}

              <label className={cn('flex flex-col gap-1', SECTION_DIVIDER, 'pt-4')}>
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Or schedule for a specific time{' '}
                  <span className="font-normal text-muted">(optional)</span>
                </span>
                <InputField
                  type="datetime-local"
                  value={engagesAtInput}
                  onChange={(e) => {
                    setEngagesAtInput(e.target.value);
                    setPristine(false);
                    setError(null);
                  }}
                />
                {engagesAtFormatted ? (
                  <span className="text-xs text-muted">
                    Stored as: {engagesAtFormatted.local}{' '}
                    <span className="text-zinc-300 dark:text-zinc-600">
                      ({engagesAtFormatted.utc})
                    </span>
                  </span>
                ) : null}
                <span className="text-xs text-muted">
                  Your browser&apos;s local timezone is used. The UTC instant is stored and shown
                  above. Must be in the future. Leave blank to disable scheduling.
                </span>
              </label>
            </div>
          </AdminSectionCard>
        ) : null}

        <AnnouncementBannerPanel />
      </div>
    </>
  );

  return (
    <AdminSceneShell
      sceneName="MaintenanceScene"
      title="Maintenance"
      description="Maintenance lobby and scheduled engage controls."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">{panel}</Commentable>
    </AdminSceneShell>
  );
}

// parity: computeEffectiveOn used in transform tests / seed validation.
void computeEffectiveOn;
